import React, {
    createContext,
    useContext,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useAssistantIntegration} from '../AssistantIntegration';
import {useGetAssistantInfo} from '../api/threadHooks';
import useLocalStorage from '../hooks/useLocalStorage';
import {createPendingPromptBox, PendingPrompt} from '../pendingPrompt';
import {AssistantInfo} from '../api/types';

/** The assistant the panel shows unless the user switches (sysadmins can). */
export const DEFAULT_ASSISTANT_KEY = 'certavue-assistant';

/**
 * Whether "Ask the assistant" can work right now, for surfaces outside the panel:
 * hidden = governance hides it (no panel at all); unavailable = the panel shows but
 * can't answer (no model, no key, allowance used up), with the reason to show.
 */
export interface AssistantReadiness {
    status: 'loading' | 'ready' | 'unavailable' | 'hidden';
    reason?: string | null;
}

// eslint-disable-next-line react-refresh/only-export-components
export const readinessOf = (
    info: AssistantInfo | undefined,
    isLoading: boolean,
    isError: boolean
): AssistantReadiness => {
    if (isError) return {status: 'hidden'};
    if (isLoading || !info) return {status: 'loading'};
    if (info.budget?.level === 'exhausted') {
        return {status: 'unavailable', reason: info.budget.reason};
    }
    if (!info.providerReady) {
        return {status: 'unavailable', reason: info.providerNotReadyReason};
    }
    return {status: 'ready'};
};

export interface IAssistantPanelContext {
    currentAssistantKeyOrId: string;
    /** A signed-in customer user: the panel (or its pull-out tab) is on every app page. */
    isPanelOpen: boolean;
    isPanelMinimized: boolean;
    /**
     * False once the assistant-info query has settled in error - the server 404s
     * assistants hidden by governance (tenant AI policy disabled, assistant disabled for
     * the customer, unpublished, sysadmin-only), so neither the panel nor its pull-out
     * tab renders. True while loading to avoid tab flicker.
     */
    isAssistantAvailable: boolean;
    /** The conversation in view, remembered per user and assistant across pages and refreshes. */
    selectedThreadId: string | undefined;
    openAssistant: (keyOrId: string) => void;
    /** Back to the default assistant. */
    closeAssistant: () => void;
    canCloseAssistant: boolean;
    minimizePanel: () => void;
    restorePanel: () => void;
    handleThreadSelect: (threadId: string) => void;
    handleNewThread: () => void;
    handleThreadCreated: (newThreadId: string) => void;
    /**
     * Opens the panel on a fresh conversation and sends the text as the user's
     * first message (e.g. "Help me get set up"). Nothing is sent when the
     * assistant can't answer; the panel shows why instead.
     */
    askAssistant: (text: string) => void;
    /** The prompt askAssistant left for the panel to send (pass to AssistantPanel). */
    pendingPrompt: PendingPrompt;
    assistantReadiness: AssistantReadiness;
}

const AssistantPanelContext = createContext<IAssistantPanelContext | undefined>(
    undefined
);

interface AssistantPanelProviderProps {
    children: ReactNode;
}

/** Per-user panel state that survives navigation and refresh. */
interface PanelMemory {
    assistant: string;
    /** Assistant key or id to the conversation last open with it. */
    threads: Record<string, string>;
}

const EMPTY_MEMORY: PanelMemory = {
    assistant: DEFAULT_ASSISTANT_KEY,
    threads: {}
};

export const AssistantPanelProvider: React.FC<AssistantPanelProviderProps> = ({
    children
}) => {
    const {userStorageKey} = useAssistantIntegration();

    // Every assistant endpoint needs PersonId + CustomerId claims and a run resolves its
    // AI key per customer, so the panel exists only for a signed-in customer user. Some
    // pages are public (e.g. /documents/*): an anonymous reader gets no panel at all.
    const isPanelOpen = !!userStorageKey;
    const user = userStorageKey ?? 'anon';

    const [memory, setMemory] = useLocalStorage<PanelMemory>(
        `assistant-panel:${user}`,
        EMPTY_MEMORY
    );
    const currentAssistantKeyOrId = memory.assistant || DEFAULT_ASSISTANT_KEY;
    const selectedThreadId = memory.threads[currentAssistantKeyOrId];

    // Expanded/collapsed preference, remembered per user. Collapsed by default: the page
    // shows the pull-out tab until the user opens it.
    const [isPanelExpanded, setIsPanelExpanded] = useLocalStorage<boolean>(
        `assistant-panel-expanded:${user}`,
        false
    );
    const isPanelMinimized = !isPanelExpanded;

    // react-query dedupes this with the panel's own identical query (['assistant-info', key]),
    // so learning availability costs no extra request.
    const assistantInfoQuery = useGetAssistantInfo(
        isPanelOpen ? currentAssistantKeyOrId : null
    );
    const isAssistantAvailable = !assistantInfoQuery.isError;
    const assistantReadiness = useMemo(
        () =>
            readinessOf(
                assistantInfoQuery.data,
                assistantInfoQuery.isLoading,
                assistantInfoQuery.isError
            ),
        [
            assistantInfoQuery.data,
            assistantInfoQuery.isLoading,
            assistantInfoQuery.isError
        ]
    );

    // A remembered assistant the user can no longer open (a sysadmin-only one after the
    // role was removed, say) falls back to the default rather than hiding the panel.
    useEffect(() => {
        if (
            assistantInfoQuery.isError &&
            currentAssistantKeyOrId !== DEFAULT_ASSISTANT_KEY
        ) {
            setMemory(previous => ({
                ...previous,
                assistant: DEFAULT_ASSISTANT_KEY
            }));
        }
    }, [assistantInfoQuery.isError, currentAssistantKeyOrId, setMemory]);

    const openAssistant = useCallback(
        (keyOrId: string) => {
            setMemory(previous => ({...previous, assistant: keyOrId}));
            setIsPanelExpanded(true); // An explicit open always expands (and is remembered)
        },
        [setMemory, setIsPanelExpanded]
    );

    const closeAssistant = useCallback(() => {
        setMemory(previous => ({
            ...previous,
            assistant: DEFAULT_ASSISTANT_KEY
        }));
    }, [setMemory]);

    const minimizePanel = useCallback(() => {
        setIsPanelExpanded(false);
    }, [setIsPanelExpanded]);

    const restorePanel = useCallback(() => {
        setIsPanelExpanded(true);
    }, [setIsPanelExpanded]);

    const rememberThread = useCallback(
        (threadId: string | undefined) => {
            setMemory(previous => {
                const assistant = previous.assistant || DEFAULT_ASSISTANT_KEY;
                const threads = {...previous.threads};
                if (threadId) {
                    threads[assistant] = threadId;
                } else {
                    delete threads[assistant];
                }
                return {...previous, threads};
            });
        },
        [setMemory]
    );

    const handleThreadSelect = useCallback(
        (threadId: string) => rememberThread(threadId),
        [rememberThread]
    );

    const handleNewThread = useCallback(
        () => rememberThread(undefined),
        [rememberThread]
    );

    const handleThreadCreated = useCallback(
        (newThreadId: string) => rememberThread(newThreadId),
        [rememberThread]
    );

    const promptBox = useRef(createPendingPromptBox());
    const [promptNonce, setPromptNonce] = useState(0);

    const askAssistant = useCallback(
        (text: string) => {
            promptBox.current.put(text);
            rememberThread(undefined); // a fresh conversation, never mid-run
            setIsPanelExpanded(true);
            setPromptNonce(n => n + 1);
        },
        [rememberThread, setIsPanelExpanded]
    );

    const pendingPrompt = useMemo<PendingPrompt>(
        () => ({nonce: promptNonce, take: () => promptBox.current.take()}),
        [promptNonce]
    );

    const contextValue = useMemo<IAssistantPanelContext>(
        () => ({
            currentAssistantKeyOrId,
            isPanelOpen,
            isPanelMinimized,
            isAssistantAvailable,
            selectedThreadId,
            openAssistant,
            closeAssistant,
            canCloseAssistant:
                currentAssistantKeyOrId !== DEFAULT_ASSISTANT_KEY,
            minimizePanel,
            restorePanel,
            handleThreadSelect,
            handleNewThread,
            handleThreadCreated,
            askAssistant,
            pendingPrompt,
            assistantReadiness
        }),
        [
            askAssistant,
            pendingPrompt,
            assistantReadiness,
            currentAssistantKeyOrId,
            isPanelOpen,
            isPanelMinimized,
            isAssistantAvailable,
            selectedThreadId,
            openAssistant,
            closeAssistant,
            minimizePanel,
            restorePanel,
            handleThreadSelect,
            handleNewThread,
            handleThreadCreated
        ]
    );

    return (
        <AssistantPanelContext.Provider value={contextValue}>
            {children}
        </AssistantPanelContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAssistantPanel = (): IAssistantPanelContext => {
    const context = useContext(AssistantPanelContext);
    if (context === undefined) {
        throw new Error(
            'useAssistantPanel must be used within an AssistantPanelProvider'
        );
    }

    return context;
};
